import type { Project, IProductElement } from '@shared/models/project.model';
import type { Results } from '@shared/models/unit.model';
import type { ElementPath } from '@shared/helpers/recursive-element.helpers';
import { getElementCategoryId } from '@shared/helpers/element-category-id.helpers';
import { getHeatedArea } from '@shared/helpers/energy/energy.helpers';
import {
  getActivityMixFromDesign,
  getLargestProgramActivityTypeFromActivityMix,
} from '@shared/helpers/design/activity-mix.helpers';
import { getBuildingDesignFromProjectOrBuilding } from '@shared/helpers/design/design.helpers';
import { getActivityIdFromActivityType } from '@shared/helpers/design/expressions/storey-sum-from-design.helpers';
import { getGenericProductCharacteristics } from '@shared/helpers/results/generic-product-characteristics.helpers';
import {
  S12MaterialGroup,
  S12_DEFAULT_FACTOR,
  S12_FACTOR_BY_GROUP,
  S12_GROUP_BY_CATEGORY,
  S13BuildingType,
  S13_BUILDING_TYPE_BY_ACTIVITY,
  S13_DEFAULT_BUILDING_TYPE,
  S13_SCHABLON_BY_BUILDING_TYPE,
} from './c-phase.constants';
import type { CPhaseMapContext } from './c-phase.model';

/**
 * Resolve the S12 material group from the owning product category in the path. Walks
 * ancestors nearest-first and returns the first category that maps to an S12 group
 * (mirrors {@link getC1MaterialGroup}); `undefined` when no category maps.
 */
export const getProductS12Group = (
  path: ElementPath,
): S12MaterialGroup | undefined => {
  for (let i = path.length - 1; i >= 0; i--) {
    const group = S12_GROUP_BY_CATEGORY[getElementCategoryId(path[i])];
    if (group) return group;
  }
  return undefined;
};

/**
 * Resolve the S12 emission factor (kg CO2e/kg) for a product. Precedence (DEV-3327):
 * 1. the product's explicit `characteristics.c3c4`,
 * 2. the owning product category's S12 group factor ({@link S12_GROUP_BY_CATEGORY}),
 * 3. the "Övrigt" schablon default.
 */
export const getProductS12Factor = (
  path: ElementPath,
  productElement: IProductElement,
): number => {
  const explicit = getGenericProductCharacteristics(path, productElement)?.c3c4;
  if (explicit !== undefined) return explicit;
  const group = getProductS12Group(path);
  return group ? S12_FACTOR_BY_GROUP[group] : S12_DEFAULT_FACTOR;
};

/**
 * C3-C4 waste processing + disposal for a single product's actual mass (IVL S12,
 * DEV-3327): `mass_kg * S12_factor`. Linear in mass, so per-product values sum into
 * the building total. Coverage (täckningsgrad) is applied later by the rollup.
 */
export const mapResultsByC3C4 = (
  results: Results,
  context: CPhaseMapContext,
): Results => {
  const mass = results.kg ?? 0;
  if (!mass) return results;

  const co2e = mass * getProductS12Factor(context.path, context.productElement);
  if (!co2e) return results;

  return { ...results, 'co2e_C3-C4': (results['co2e_C3-C4'] ?? 0) + co2e };
};

/** Resolve the S13 building type for a project from its activity, with a default. */
export const getS13BuildingType = (project: Project): S13BuildingType => {
  const design = getBuildingDesignFromProjectOrBuilding(project);
  const activityId = getActivityIdFromActivityType(
    getLargestProgramActivityTypeFromActivityMix(
      getActivityMixFromDesign(design),
    ),
  );
  return S13_BUILDING_TYPE_BY_ACTIVITY[activityId] ?? S13_DEFAULT_BUILDING_TYPE;
};

/**
 * Building-wide C3-C4 BD7/BD8 schablon (IVL S13, DEV-3327). Only the parts without
 * actual modelled resources are included, so a building part is never counted as both
 * an actual resource and a schablon. Scaled by Atemp via {@link getHeatedArea}. Emitted
 * once at the version level (like B7 water), so schablon parts are not upscaled by
 * coverage again.
 */
export const getC3C4SchablonResults = (
  project: Project,
  gfa: number,
  options: { includeBd7: boolean; includeBd8: boolean },
): Results => {
  const atemp = getHeatedArea(gfa);
  if (!atemp) return {};

  const schablon = S13_SCHABLON_BY_BUILDING_TYPE[getS13BuildingType(project)];
  const bd7 = options.includeBd7
    ? atemp * schablon.bd7KgPerM2 * schablon.bd7Factor
    : 0;
  const bd8 = options.includeBd8
    ? atemp * schablon.bd8KgPerM2 * schablon.bd8Factor
    : 0;

  const co2e = bd7 + bd8;
  return co2e ? { 'co2e_C3-C4': co2e } : {};
};
